/**
 * HelpSection
 *
 * Help & support page. Reachable via the sidebar profile popup's
 * "Help & support" row.
 *
 * Layout mirrors the /account-settings page: the standard list-page shell
 * (sticky `.page-header__title` + subtitle row over an `.explore-view--list`
 * scroll container) wrapping the shared `.settings-column` card column. Each
 * FAQ category renders as its own card; a final "Still need help?" card
 * exposes the Contact support CTA.
 *
 * The contact link uses a root-relative path; if the app ships on a
 * subdomain separate from the marketing site this will need to swap to
 * an absolute URL via a LANDING_URL constant.
 */

const CHEVRON_SVG = `
  <svg viewBox="0 0 16 16" aria-hidden="true" width="16" height="16">
    <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`;

const FAQ_GROUPS = [
  {
    title: 'Getting started',
    items: [
      {
        q: 'How do I start a session?',
        a: 'Type into the chat input on the home screen. You can pick a mode — Advice, Insight, or Treatment — from the mode selector above the input.'
      },
      {
        q: 'What are the different modes?',
        a: 'Advice is for working through a specific situation. Insight surfaces patterns from your past sessions. Treatment is for longer, focused therapeutic work. Discovery is a library of structured questionnaires that build your profile.'
      },
      {
        q: 'What is Discovery?',
        a: 'A library of structured reflections and questionnaires grouped into modules. Completing sections feeds the AI durable, high-confidence context about you — so future conversations start from a richer picture.'
      }
    ]
  },
  {
    title: 'Insights & memory',
    items: [
      {
        q: 'Where do my insights come from?',
        a: 'Every chat session is analyzed afterward to extract themes, patterns, and takeaways. These roll up into your long-term memory and inform future conversations.'
      },
      {
        q: 'Can I delete an individual insight or session?',
        a: 'You can delete sessions from the Search page. Bulk data export and deletion are on the roadmap; contact support in the meantime.'
      }
    ]
  },
  {
    title: 'Partner linking',
    items: [
      {
        q: 'What does partner linking do?',
        a: 'You can link with a partner so the AI understands both perspectives. Linked partners share certain couples-focused Discovery modules and get shared insights when relevant.'
      },
      {
        q: 'What does my partner see?',
        a: 'Only content from shared modules you both complete. Your individual chat sessions and personal insights remain private.'
      },
      {
        q: 'How do I unlink?',
        a: 'Partner management is coming soon. In the meantime, contact support and we\'ll disconnect your account from your partner\'s.'
      }
    ]
  },
  {
    title: 'Account & billing',
    items: [
      {
        q: 'What\'s the difference between Basic and Premium?',
        a: 'Basic includes your first two discovery paths (Starting Your Journey and Continuing Your Journey), guide previews, core insights and memory, and a daily limit on live conversation messages. Premium unlocks every discovery path and full personalized guides, and removes the daily limit. See the upgrade page for current pricing.'
      },
      {
        q: 'How do I cancel my subscription?',
        a: 'Open the profile menu in the bottom-left and choose Manage subscription. You\'ll stay on Premium until the end of your current billing period.'
      },
      {
        q: 'How do I sign out?',
        a: 'Open the profile menu in the bottom-left and choose Sign out.'
      }
    ]
  },
  {
    title: 'Privacy & data',
    items: [
      {
        q: 'Is my data used to train AI models?',
        a: 'Your conversations are used only to run encouple for you — generating your responses and building your private memory. They are processed by our AI providers to deliver the service, and we never sell your data or use it for advertising.'
      },
      {
        q: 'Who can see my conversations?',
        a: 'Only you. Our team may access account-level info to resolve support issues, but never your conversation content.'
      },
      {
        q: 'Can I export or delete my data?',
        a: 'Yes — contact support to request either. Self-serve export and deletion are planned.'
      }
    ]
  }
];

/**
 * Minimal HTML-attribute escape for placeholder copy. Sufficient here since
 * all content is hard-coded above; swap for a shared util if this ever
 * accepts user-supplied strings.
 */
function escape(str) {
  return String(str).replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[ch]);
}

export class HelpSection {
  /**
   * @param {Object} elements
   * @param {HTMLElement} elements.container - The #help-page div
   * @param {EventBus} [eventBus]
   */
  constructor(elements, eventBus) {
    this.container = elements.container;
    this.eventBus = eventBus;
    this.isActive = false;
    this.handleFaqToggle = this.handleFaqToggle.bind(this);
  }

  async activate() {
    this.isActive = true;
    this.render();
    this.attachListeners();
  }

  deactivate() {
    this.removeListeners();
    this.isActive = false;
  }

  render() {
    this.container.innerHTML = `
      <div class="explore-section">
        <div class="explore-container">
          <!-- Single-slice surface: the title is the only pinned row, so the
               base [data-sticky-slice] rule is the whole behaviour and no
               StickyStack controller is needed (see components/sticky-stack.css). -->
          <div class="explore-view--list" data-sticky-stack>
            <h2 class="page-header__title content-width" data-sticky-slice>Help</h2>

            <div class="page-header content-width">
              <div class="page-header__subtitle-row">
                <p class="page-header__subtitle">Find answers &amp; reach the team</p>
              </div>
            </div>

            <div class="settings-column">
              ${this.renderFaq()}
              ${this.renderContact()}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderFaq() {
    const cards = FAQ_GROUPS.map((group) => `
      <div class="card help-faq-card">
        <h3 class="help-faq-card__title">${escape(group.title)}</h3>
        <div class="faq__list faq__list--inset">
          ${group.items.map((item) => `
            <div class="faq__item">
              <button type="button" class="faq__question">
                <span>${escape(item.q)}</span>
                <span class="faq__icon faq__icon--chevron">${CHEVRON_SVG}</span>
              </button>
              <div class="faq__answer">
                <p>${escape(item.a)}</p>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `).join('');

    // No group label — each card's own title carries the hierarchy. The
    // wrapper stays so the FAQ cards keep a tighter rhythm between them than
    // .settings-column's gap gives the contact card below.
    return `
      <div class="help-faq-section__cards">
        ${cards}
      </div>
    `;
  }

  renderContact() {
    return `
      <div class="card help-contact-card">
        <div class="help-contact-card__body">
          <h3 class="help-contact-card__title">Still need help?</h3>
          <p class="help-contact-card__detail">Our team typically responds within a few hours.</p>
        </div>
        <a class="help-contact-card__cta" href="/contact" target="_blank" rel="noopener">
          Contact support
        </a>
      </div>
    `;
  }

  /**
   * Attach click listeners to FAQ question buttons for expand/contract.
   * @private
   */
  attachListeners() {
    const questions = this.container.querySelectorAll('.faq__question');
    questions.forEach((q) => q.addEventListener('click', this.handleFaqToggle));
  }

  /** @private */
  removeListeners() {
    const questions = this.container.querySelectorAll('.faq__question');
    questions.forEach((q) => q.removeEventListener('click', this.handleFaqToggle));
  }

  /**
   * Toggle a FAQ item open/closed. Chevron rotation handled in CSS via
   * .faq__item--open + .faq__icon--chevron.
   * @private
   */
  handleFaqToggle(event) {
    const question = event.currentTarget;
    const item = question.closest('.faq__item');
    if (!item) return;
    item.classList.toggle('faq__item--open');
  }
}

export default HelpSection;
